import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useFetchOrders } from '../../hooks/useFetchOrders'
import { ProductsListSkeleton } from '../../ui/ProductsListSkelton'
import { OrderItem } from './OrdersListItem'

const ITEMS_PER_PAGE = 20

export const OrdersList: React.FC = () => {
  const { orders, isError, isLoading, error, pagination } = useFetchOrders({
    page: 1,
    per_page: ITEMS_PER_PAGE,
  })
  const [totalPages, setTotalPages] = useState(1)

  // Calculate total pages based on orders length
  useEffect(() => {
    if (orders && orders.length === ITEMS_PER_PAGE) {
      // If we get a full page, there might be more pages
      setTotalPages((prev) => Math.max(prev, pagination.page + 1))
    } else if (orders && orders.length < ITEMS_PER_PAGE && orders.length > 0) {
      // If we get less than full page, this is the last page
      setTotalPages(pagination.page)
    }
  }, [orders, pagination.page])

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      pagination.goToPage(newPage)
    }
  }

  const renderPagination = () => {
    if (!orders || orders.length === 0) return null

    const pageNumbers = []
    const maxVisiblePages = 5
    let startPage = Math.max(
      1,
      pagination.page - Math.floor(maxVisiblePages / 2),
    )
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    // Adjust start page if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i)
    }

    return (
      <div className='flex justify-center items-center gap-2 mt-6 mb-4'>
        {/* Previous button */}
        <button
          onClick={() => handlePageChange(pagination.page - 1)}
          disabled={pagination.page === 1 || isLoading}
          className='px-3 py-2 rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          style={{
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--border-color)',
          }}
        >
          قبلی
        </button>

        {/* First page */}
        {startPage > 1 && (
          <>
            <button
              onClick={() => handlePageChange(1)}
              disabled={isLoading}
              className='px-3 py-2 rounded-md border transition-colors'
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border-color)',
              }}
            >
              1
            </button>
            {startPage > 2 && <span className='px-2'>...</span>}
          </>
        )}

        {/* Page numbers */}
        {pageNumbers.map((pageNum) => (
          <button
            key={pageNum}
            onClick={() => handlePageChange(pageNum)}
            disabled={isLoading}
            className={`px-3 py-2 rounded-md border transition-colors ${
              pageNum === pagination.page ? 'font-bold' : ''
            }`}
            style={{
              backgroundColor:
                pageNum === pagination.page
                  ? 'var(--accent-secondary)'
                  : 'var(--bg-secondary)',
              borderColor: 'var(--border-color)',
              color: pageNum === pagination.page ? 'white' : 'inherit',
            }}
          >
            {pageNum}
          </button>
        ))}

        {/* Last page */}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className='px-2'>...</span>}
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={isLoading}
              className='px-3 py-2 rounded-md border transition-colors'
              style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border-color)',
              }}
            >
              {totalPages}
            </button>
          </>
        )}

        {/* Next button */}
        <button
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={pagination.page >= totalPages || isLoading}
          className='px-3 py-2 rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          style={{
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--border-color)',
          }}
        >
          بعدی
        </button>
      </div>
    )
  }

  return (
    <div className='my-10 container'>
      <Helmet>
        <title className='text-xl font-semibold font-semibold'>
          لیست سفارشات من
        </title>
        <meta
          name='description'
          content='بوک استوری - هر کتاب یک داستان، هر داستان یک زندگی دوباره'
        />
      </Helmet>
      <h1
        className='my-4 text-xl font-bold text-white p-5 rounded-md'
        style={{ backgroundColor: 'var(--accent-secondary)' }}
      >
        لیست سفارشات من
      </h1>
      {isError && <h3 className='text-center my-4'>{error?.message}</h3>}
      {isLoading && <ProductsListSkeleton />}

      <div className='mt-8'>
        {orders && orders?.length > 0 ? (
          <>
            <ul>
              <li className='flex font-bold justify-around mb-4 py-0 px-4 rounded-md'>
                <div className='w-1/4'>کد سفارش</div>
                <div className='w-1/4'>تاریخ سفارش</div>
                <div className='w-1/4'>مبلغ کل</div>
                <div className='w-1/4'>وضعیت سفارش</div>
              </li>
              {orders.map((order: any) => (
                <OrderItem key={order.id} order={order} />
              ))}
            </ul>
            {renderPagination()}
          </>
        ) : (
          !isLoading && (
            <p className='px-4'>شما هنوز هیچ سفارشی ثبت نکرده اید.</p>
          )
        )}
      </div>
    </div>
  )
}
